import { useCallback } from 'react';

import { gql, useApolloClient, useMutation } from '@apollo/client';
import { getStoreKeyName } from '@apollo/client/utilities';

import { useTeam } from '../../context/TeamProvider';
import type {
  NexoyaContentLabelCategoryUpdateInput,
  NexoyaMutationCreateContentLabelCategoryArgs,
  NexoyaMutationDeleteContentLabelCategoryArgs,
  NexoyaMutationUpdateContentLabelCategoryArgs,
} from '../../types';

import { CONTENT_LABEL_MUTATION_CONTEXT, activeStageReads, contentLabelCategoryCacheId } from './mutationEffects';
import { CONTENT_LABEL_CATEGORY_FRAGMENT, type ContentLabelCategoryListItem } from './queryContentLabelCategories';
import { evictDeletePreviews } from './queryContentLabelDeletePreviews';
import { isStageReadsStoreField } from './stageReads';

/**
 * Category CRUD.
 *
 * Cache effects rather than refetching the list — the page never waits on a
 * round-trip it did not ask for, and a failed refetch cannot turn a successful
 * save into an error:
 * - update: the returned category is normalized (`ContentLabelCategory` is keyed
 *   by its id), so every read of it updates by itself.
 * - create: a reference to the new category is appended to the cached list. The
 *   stage reads keep their own category arrays (`STAGE_READS_CONNECTION_KEY`),
 *   which lack it: those are dropped, and the ones on screen asked again, so the
 *   new category gets counts and pages of its own (see `mutationEffects.ts`).
 * - delete: the category is evicted and everything only it referenced (its
 *   values) is garbage-collected. A `false` answer deleted nothing, so the cache
 *   is left alone.
 *
 * Every mutation opts out of the global error toast: the labels UI surfaces each
 * failure itself, exactly once, where the user acted. No `onError` option either
 * — with one, Apollo 3.13 resolves `mutate` on failure instead of rejecting.
 */

export const CREATE_CONTENT_LABEL_CATEGORY_MUTATION = gql`
  mutation CreateContentLabelCategory($teamId: Int!, $create: ContentLabelCategoryCreateInput!) {
    createContentLabelCategory(teamId: $teamId, create: $create) {
      ...ContentLabelCategoryFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_FRAGMENT}
`;

export const UPDATE_CONTENT_LABEL_CATEGORY_MUTATION = gql`
  mutation UpdateContentLabelCategory($teamId: Int!, $update: ContentLabelCategoryUpdateInput!) {
    updateContentLabelCategory(teamId: $teamId, update: $update) {
      ...ContentLabelCategoryFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_FRAGMENT}
`;

export const DELETE_CONTENT_LABEL_CATEGORY_MUTATION = gql`
  mutation DeleteContentLabelCategory($teamId: Int!, $delete: ContentLabelCategoryDeleteInput!) {
    deleteContentLabelCategory(teamId: $teamId, delete: $delete)
  }
`;

export type CreateContentLabelCategoryResponse = { createContentLabelCategory: ContentLabelCategoryListItem };
export type UpdateContentLabelCategoryResponse = { updateContentLabelCategory: ContentLabelCategoryListItem };
export type DeleteContentLabelCategoryResponse = { deleteContentLabelCategory: boolean };

export function useCreateContentLabelCategory() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    CreateContentLabelCategoryResponse,
    NexoyaMutationCreateContentLabelCategoryArgs
  >(CREATE_CONTENT_LABEL_CATEGORY_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const createContentLabelCategory = useCallback(
    ({ name, color }: { name: string; color: string }) =>
      mutate({
        variables: { teamId, create: { name, color } },
        update(cache, { data }) {
          const created = data?.createContentLabelCategory;
          if (!created) return;

          // The list is stored once per team (`listContentLabelCategories({"teamId":…})`);
          // only this team's list gains the category.
          const listForTeam = getStoreKeyName('listContentLabelCategories', { teamId });

          cache.modify<{ listContentLabelCategories: ContentLabelCategoryListItem[] }>({
            fields: {
              listContentLabelCategories(existing = [], { storeFieldName, readField, toReference, DELETE }) {
                // Every stage read's array lacks the new category, and a read of one that is
                // not on screen would come back complete without it (its counts unknown for
                // good, its stage table empty) — so it is dropped and re-read when next shown.
                if (isStageReadsStoreField(storeFieldName)) return DELETE;
                if (storeFieldName !== listForTeam) return existing;
                const alreadyListed = existing.some(
                  (ref) => readField('contentLabelCategoryId', ref) === created.contentLabelCategoryId,
                );
                const createdRef = toReference(created);
                return alreadyListed || !createdRef ? existing : [...existing, createdRef];
              },
            },
          });
        },
        // The stage reads on screen are asked again NOW — also the one whose answer, computed
        // before the create, may still be on the wire.
        refetchQueries: ({ data }) => (data?.createContentLabelCategory ? activeStageReads(client) : []),
      }),
    [client, mutate, teamId],
  );

  return [createContentLabelCategory, result] as const;
}

export function useUpdateContentLabelCategory() {
  const { teamId } = useTeam();

  const [mutate, result] = useMutation<
    UpdateContentLabelCategoryResponse,
    NexoyaMutationUpdateContentLabelCategoryArgs
  >(UPDATE_CONTENT_LABEL_CATEGORY_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const updateContentLabelCategory = useCallback(
    (update: NexoyaContentLabelCategoryUpdateInput) => mutate({ variables: { teamId, update } }),
    [mutate, teamId],
  );

  return [updateContentLabelCategory, result] as const;
}

export function useDeleteContentLabelCategory() {
  const { teamId } = useTeam();

  const [mutate, result] = useMutation<
    DeleteContentLabelCategoryResponse,
    NexoyaMutationDeleteContentLabelCategoryArgs
  >(DELETE_CONTENT_LABEL_CATEGORY_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const deleteContentLabelCategory = useCallback(
    ({ contentLabelCategoryId }: { contentLabelCategoryId: string }) =>
      mutate({
        variables: { teamId, delete: { contentLabelCategoryId } },
        update(cache, { data }) {
          if (!data?.deleteContentLabelCategory) return;

          // Lists drop a reference to an evicted entity on read, so the category
          // disappears from every document; gc then collects its values.
          cache.evict({ id: contentLabelCategoryCacheId(cache, contentLabelCategoryId) });
          // "Delete category and constraints" can change what the other categories' previews count.
          evictDeletePreviews(cache);
          cache.gc();
        },
      }),
    [mutate, teamId],
  );

  return [deleteContentLabelCategory, result] as const;
}
